import { useCallback, useEffect, useRef, useState } from "react";
import { useAnimatedToastStack } from "../beui/AnimatedToastStack";
import { api } from "../../lib/api";
import { ApiError } from "../../lib/api-client/transport";
import type { SupportConversation } from "../../lib/api-client/support";
import { useSupportRead, supportAccessLost, supportError } from "./useSupportRead";

/** Own the conversation's acknowledgement, retry and refusal transitions for both member and Admin views.
 * Image reads report refusal here; their byte URLs remain owned by the image module. */
export function useSupportConversation(id: string, admin: boolean) {
  const read = useCallback(() => admin ? api.adminTicket(id) : api.ticket(id), [admin, id]);
  const { data, setData, error, loading, reload } = useSupportRead(read);
  const [reply, setReply] = useState("");
  const [outcome, setOutcome] = useState("");
  const [attempt, setAttempt] = useState<{ body: string; requestId: string; images: File[] } | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [imageDenied, setImageDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState("");
  const { toasts, showToast, dismissToast, clearToasts } = useAnimatedToastStack();
  const [confirming, setConfirming] = useState(false);
  const [unreviewed, setUnreviewed] = useState<SupportConversation | null>(null);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const clearProtectedDraft = useCallback(() => {
    setReply(""); setImages([]); setOutcome(""); setAttempt(null); setSendError("");
    clearToasts(); setConfirming(false); setUnreviewed(null);
  }, [clearToasts]);
  useEffect(() => {
    // The read owner already removed denied content; setData here would erase its refusal error.
    if (supportAccessLost(error)) clearProtectedDraft();
  }, [error, clearProtectedDraft]);
  function failure(cause: unknown) {
    if (!active.current) return;
    if (supportAccessLost(cause)) { clearProtectedDraft(); setData(null); void reload(); }
    else if (cause instanceof ApiError && cause.code === "support_review_required") setUnreviewed(data);
    else if (cause instanceof ApiError && cause.code === "support_resolved") {
      setAttempt(null); setConfirming(false);
      if (data) setData({ ...data, ticket: { ...data.ticket, status: "resolved" } });
      void reload();
    }
    else if (cause instanceof ApiError && ([400, 413].includes(cause.status) || cause.code === "support_upload_failed")) setAttempt(null);
    setSendError(supportError(cause));
  }
  async function send() {
    if (busy || loading || error || !data) return;
    const command = attempt || { body: reply.trim(), requestId: crypto.randomUUID(), images };
    if (!command.body && !command.images.length) return;
    setAttempt(command); setBusy(true); setSendError(""); clearToasts();
    try {
      const result = admin ? await api.adminReplyTicket(id, command.body, command.requestId, command.images) : await api.replyTicket(id, command.body, command.requestId, command.images);
      if (!active.current) return;
      // The saved messages are the acknowledgement. No second read can turn a saved send into a failure.
      setData({ ticket: result.ticket, messages: result.messages });
      setReply(""); setImages([]); setAttempt(null); showToast({ status: "success", title: "Reply sent." });
    } catch (cause) { failure(cause); }
    finally { if (active.current) setBusy(false); }
  }
  async function resolve() {
    if (busy || loading || error || !data || data === unreviewed || !outcome.trim()) return;
    setBusy(true); setSendError(""); clearToasts();
    try {
      const result = await api.adminSetTicketStatus(id, "resolved", outcome.trim(), undefined, data.messages.filter(message => message.byRole === "member").length);
      if (!active.current) return;
      setData({ ...data, ticket: result.ticket });
      setConfirming(false); setAttempt(null); showToast({ status: "success", title: "Ticket resolved." });
    } catch (cause) { failure(cause); }
    finally { if (active.current) setBusy(false); }
  }
  function imageAccessLost(cause: unknown) {
    if (!supportAccessLost(cause)) return false;
    clearProtectedDraft(); setData(null); setImageDenied(true);
    return true;
  }
  function refresh() { setImageDenied(false); void reload(); }
  function requestResolution() { setSendError(""); setConfirming(true); }
  return {
    data, error, loading, reply, setReply, outcome, setOutcome, images, setImages,
    retryPending: !!attempt, busy, sendError, confirming, imageDenied,
    resolutionBlocked: data === unreviewed, toasts, dismissToast,
    send, resolve, refresh, imageAccessLost, requestResolution, closeResolution: () => setConfirming(false),
  };
}
